import { ZoomIn, ZoomOut, Maximize, Grid3x3 as Grid3X3, Magnet, Undo2, Redo2, Eye, EyeOff, Layers, SlidersHorizontal, Ruler, AlignVerticalSpaceAround } from 'lucide-react';
import { useCanvasStore } from '../../stores/canvasStore';
import { useViewportStore } from '../../stores/viewportStore';
import { useStudioStore } from '../../stores/studioStore';
import ArtboardPresetSelector from './ArtboardPresetSelector';
import type { RulerUnit } from '../../types';

export default function EditorToolbar() {
  const {
    canvasWidth,
    canvasHeight,
    fabricCanvas,
    showBackgroundLayer,
    showOverlayLayer,
    toggleBackgroundLayer,
    toggleOverlayLayer,
    toggleLayerPanel,
    togglePropertyPanel,
    showLayerPanel,
    showPropertyPanel,
    showRulers,
    toggleRulers,
  } = useCanvasStore();

  const {
    zoom,
    zoomIn,
    zoomOut,
    zoomReset,
    fitToScreen,
    gridVisible,
    toggleGrid,
    snapEnabled,
    toggleSnap,
    showGuides,
    guides,
    rulerUnit,
  } = useViewportStore();

  const { undo, redo, undoStack, redoStack } = useStudioStore();

  return (
    <div className="h-9 bg-[#141414] border-b border-white/[0.06] flex items-center px-3 gap-1 shrink-0">
      <div className="flex items-center gap-0.5">
        <button
          onClick={undo}
          disabled={undoStack.length === 0}
          className="p-1.5 rounded-md text-zinc-500 hover:text-zinc-300 hover:bg-white/[0.06] transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
          title="Undo (Ctrl+Z)"
        >
          <Undo2 size={14} />
        </button>
        <button
          onClick={redo}
          disabled={redoStack.length === 0}
          className="p-1.5 rounded-md text-zinc-500 hover:text-zinc-300 hover:bg-white/[0.06] transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
          title="Redo (Ctrl+Shift+Z)"
        >
          <Redo2 size={14} />
        </button>
      </div>

      <div className="w-px h-4 bg-white/[0.06] mx-1" />

      <div className="flex items-center gap-0.5">
        <button
          onClick={toggleGrid}
          className={`p-1.5 rounded-md transition-colors ${
            gridVisible ? 'text-cyan-400 bg-cyan-500/10' : 'text-zinc-500 hover:text-zinc-300 hover:bg-white/[0.06]'
          }`}
          title="Toggle Grid (G)"
        >
          <Grid3X3 size={14} />
        </button>
        <button
          onClick={toggleSnap}
          className={`p-1.5 rounded-md transition-colors ${
            snapEnabled ? 'text-cyan-400 bg-cyan-500/10' : 'text-zinc-500 hover:text-zinc-300 hover:bg-white/[0.06]'
          }`}
          title="Snap to Grid"
        >
          <Magnet size={14} />
        </button>
        <button
          onClick={toggleRulers}
          className={`p-1.5 rounded-md transition-colors ${
            showRulers ? 'text-cyan-400 bg-cyan-500/10' : 'text-zinc-500 hover:text-zinc-300 hover:bg-white/[0.06]'
          }`}
          title="Toggle Rulers (Ctrl+R)"
        >
          <Ruler size={14} />
        </button>
        <button
          onClick={() => useViewportStore.getState().toggleGuides()}
          className={`p-1.5 rounded-md transition-colors ${
            showGuides ? 'text-cyan-400 bg-cyan-500/10' : 'text-zinc-500 hover:text-zinc-300 hover:bg-white/[0.06]'
          }`}
          title="Toggle Guides (Ctrl+;)"
        >
          <AlignVerticalSpaceAround size={14} />
        </button>
        {showRulers && (
          <select
            value={rulerUnit}
            onChange={(e) => useViewportStore.getState().setRulerUnit(e.target.value as RulerUnit)}
            className="px-1 py-0.5 text-[10px] bg-white/[0.04] border border-white/[0.08] rounded text-zinc-500 focus:outline-none focus:border-cyan-500/50 cursor-pointer"
            title="Ruler unit"
          >
            <option value="px">px</option>
            <option value="in">in</option>
            <option value="cm">cm</option>
            <option value="mm">mm</option>
          </select>
        )}
        {guides.length > 0 && (
          <button
            onClick={() => useViewportStore.getState().clearAllGuides()}
            className="px-1.5 py-0.5 text-[10px] text-zinc-600 hover:text-red-400 hover:bg-red-500/10 rounded transition-colors"
            title="Clear all guides"
          >
            Clear ({guides.length})
          </button>
        )}
      </div>

      <div className="w-px h-4 bg-white/[0.06] mx-1" />

      <div className="flex items-center gap-0.5">
        <button
          onClick={toggleBackgroundLayer}
          className={`p-1.5 rounded-md transition-colors flex items-center gap-1 ${
            showBackgroundLayer ? 'text-blue-400 bg-blue-500/10' : 'text-zinc-500 hover:text-zinc-300 hover:bg-white/[0.06]'
          }`}
          title="Toggle Background Layer"
        >
          {showBackgroundLayer ? <Eye size={13} /> : <EyeOff size={13} />}
          <span className="text-[10px] font-medium uppercase tracking-wider">BG</span>
        </button>
        <button
          onClick={toggleOverlayLayer}
          className={`p-1.5 rounded-md transition-colors flex items-center gap-1 ${
            showOverlayLayer ? 'text-emerald-400 bg-emerald-500/10' : 'text-zinc-500 hover:text-zinc-300 hover:bg-white/[0.06]'
          }`}
          title="Toggle Overlay Layer"
        >
          {showOverlayLayer ? <Eye size={13} /> : <EyeOff size={13} />}
          <span className="text-[10px] font-medium uppercase tracking-wider">OV</span>
        </button>
      </div>

      <div className="w-px h-4 bg-white/[0.06] mx-1" />

      <ArtboardPresetSelector />

      <div className="flex-1" />

      <div className="flex items-center gap-0.5">
        <button
          onClick={toggleLayerPanel}
          className={`p-1.5 rounded-md transition-colors ${
            showLayerPanel ? 'text-cyan-400 bg-cyan-500/10' : 'text-zinc-500 hover:text-zinc-300 hover:bg-white/[0.06]'
          }`}
          title="Toggle Layer Panel"
        >
          <Layers size={14} />
        </button>
        <button
          onClick={togglePropertyPanel}
          className={`p-1.5 rounded-md transition-colors ${
            showPropertyPanel ? 'text-cyan-400 bg-cyan-500/10' : 'text-zinc-500 hover:text-zinc-300 hover:bg-white/[0.06]'
          }`}
          title="Toggle Property Panel"
        >
          <SlidersHorizontal size={14} />
        </button>
      </div>

      <div className="w-px h-4 bg-white/[0.06] mx-1" />

      <div className="flex items-center gap-0.5">
        <button
          onClick={zoomOut}
          className="p-1.5 rounded-md text-zinc-500 hover:text-zinc-300 hover:bg-white/[0.06] transition-colors"
          title="Zoom Out (Ctrl+-)"
        >
          <ZoomOut size={14} />
        </button>
        <button
          onClick={zoomReset}
          className="px-2 py-0.5 rounded-md text-[11px] font-mono text-zinc-400 hover:text-zinc-200 hover:bg-white/[0.06] transition-colors min-w-[44px] text-center"
          title="Reset to 100% (Ctrl+0)"
        >
          {Math.round(zoom * 100)}%
        </button>
        <button
          onClick={zoomIn}
          className="p-1.5 rounded-md text-zinc-500 hover:text-zinc-300 hover:bg-white/[0.06] transition-colors"
          title="Zoom In (Ctrl+=)"
        >
          <ZoomIn size={14} />
        </button>
        <button
          onClick={() => {
            const el = (fabricCanvas as any)?.lowerCanvasEl as HTMLCanvasElement | undefined;
            if (el) fitToScreen(canvasWidth, canvasHeight, el.clientWidth, el.clientHeight);
          }}
          className="p-1.5 rounded-md text-zinc-500 hover:text-zinc-300 hover:bg-white/[0.06] transition-colors"
          title="Fit to Screen (Ctrl+1)"
        >
          <Maximize size={14} />
        </button>
      </div>
    </div>
  );
}
